<template>
	<section class="markets-trending-topics">
		<Subtitle
			weight="medium"
			class="mb-4 lg:mb-6 title-font"
		>
			Trending Topics
		</Subtitle>

		<div
			class="flex gap-2 w-full overflow-x-auto hide-scrollbar lg:overflow-hidden scrollbar-hide border-b border-slate-100 pb-2 lg:pb-6"
		>
			<TabButton
				v-for="tab in tabs"
				:key="tab"
				:label="tab"
				:active="activeTab === tab"
				@click="handleTabChange(tab)"
			/>
		</div>

		<!-- Loading state -->
		<TrendingTopicsSkeleton v-if="loading" />

		<div
			v-else-if="stories && stories.length > 0"
			class="lg:grid lg:grid-cols-3"
		>
			<TopicCard
				:image="stories[0].image"
				:title="decode(stories[0].title)"
				:description="stripHtml(decode(stories[0].content))"
				:stock_pairs="stories[0].stock_pairs"
				:to="`/news/${stories[0].slug}/`"
				featured
				class="lg:mb-0 pr-5 border-b lg:border-b-0"
			/>
			<div class="col-span-2 grid grid-cols-1 md:grid-cols-2">
				<div
					v-for="(topic, index) in stories.slice(1)"
					:key="topic.title"
					class="lg:px-5 border-b lg:border-b-0 lg:border-l border-slate-100"
				>
					<TopicCard
						:image="topic.image"
						:title="decode(topic.title)"
						:description="stripHtml(decode(topic.content))"
						:stock_pairs="topic.stock_pairs"
						:to="`/news/${topic.slug}/`"
						:class="{ 'lg:border-b border-slate-100': index < 4 }"
					/>
				</div>
			</div>
		</div>

		<div
			v-else
			class="flex items-center justify-center text-sm text-center text-gray-400 min-h-[300px]"
		>
			No data available at the moment, check again later.
		</div>
	</section>
</template>
<script setup>
import { ref, onMounted } from "vue";
import axios from "axios";
import he from "he";
import TopicCard from "./TopicCard.vue";

import TabButton from "./TabButton.vue";
import TrendingTopicsSkeleton from "~/components/skeleton/TrendingTopicsSkeleton.vue";
import { useStocksStore } from "~/stores/useStocksStore";

const { songbirdProxy } = useRuntimeConfig().public;

const stripHtml = (html) => html.replace(/<[^>]*>/g, "").trim();

const decode = he.decode;
const props = defineProps({
	tabs: {
		type: Array,
		default: () => [
			"AI & Tech",
			"Real Estate",
			"Energy",
			"Healthcare",
			"Consumer Goods",
			"Industrials"
		]
	}
});

const stocksStore = useStocksStore();

// Create mapping from display names to backend sector names
const tabToSectorMapping = {
	"AI & Tech": "Technology",
	"Real Estate": "Real Estate",
	Energy: "Energy",
	Healthcare: "Healthcare",
	"Consumer Goods": "Consumer Staples",
	Industrials: "Industrials"
};

// Component state
const activeTab = ref("AI & Tech");
const stories = ref([]);
const loading = ref(false);

// Fetch trending topics data
const fetchTrendingTopicsData = async (sector) => {
	try {
		loading.value = true;

		const response = await axios.get(`${songbirdProxy}stocks/news/filtered`, {
			params: {
				value: sector,
				is_sector: true
			}
		});

		const responseData = response?.data;
		if (!responseData || typeof responseData !== "object") {
			return [];
		}

		const keys = Object.keys(responseData);

		const storiesArray = keys
			.map((key) => {
				const story = responseData[key]?.[0];
				if (!story) return null;
				return {
					...story,
					stock_pairs: story.stock_pairs || []
				};
			})
			.filter(Boolean)
			.slice(0, 7);

		return storiesArray;
	} catch (error) {
		console.warn("[MarketsTrendingTopics] Failed to fetch data:", error?.message || error);
		return [];
	} finally {
		loading.value = false;
	}
};

// Handle tab change
const handleTabChange = async (tab) => {
	activeTab.value = tab;
	const sector = tabToSectorMapping[tab];

	if (sector) {
		const fetchedStories = await fetchTrendingTopicsData(sector);
		stories.value = fetchedStories;

		fetchedStories.forEach((story) => {
			if (story.stock_pairs && story.stock_pairs[1]) {
				stocksStore.addSubscribedSymbolsFromPairs(story.stock_pairs);
			}
		});
	}
};

// Initialize with first tab on mount
onMounted(async () => {
	if (props.tabs.length > 0) {
		await handleTabChange(props.tabs[0]);
	}
});
</script>
